import { useQuery } from '@apollo/client/react'
import { UntitledIcon } from '@faceup/icons'
import { ulDownload01 } from '@faceup/icons/ulDownload01'
import { CopyButton, twMerge } from '@faceup/ui'
import { Button, Segmented, Skeleton, Tag, Tooltip, Typography } from '@faceup/ui-base'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { escapeBody } from 'fetch-to-curl'
import { Fragment, type ReactNode, useState } from 'react'
import { graphql } from '../../__generated__'
import { type FragmentType, getFragmentData } from '../../__generated__/fragment-masking'
import {
  buildCurl,
  buildCurlChunks,
  buildHttp,
  buildHttpParts,
  decodeJwtPayload,
  downloadLogsAsJsonl,
  formatBytes,
  getAbsoluteUrl,
  getContentType,
  headerEntriesOf,
  INDENT,
  methodTagColors,
  renderJson,
} from './integrationHttpLogsUtils'

// All the fields this detail renders
const detailFragment = graphql(`
  fragment IntegrationHttpLogDetail_log on IntegrationHttpLog {
    id
    direction
    provider
    method
    url
    institutionId
    institutionName
    statusCode
    durationMs
    requestHeaders
    requestBody
    requestBodySize
    responseHeaders
    responseBody
    responseBodySize
    errorMessage
    createdAt
  }
`)

// Headers and bodies are loaded only for the row a user opens: the list would otherwise carry
// every body on the page, megabytes for a page of HR sync calls
const detailQuery = graphql(`
  query IntegrationHttpLogDetailQuery($id: UUID!) {
    integrationHttpLog(id: $id) {
      id
      ...IntegrationHttpLogDetail_log
    }
  }
`)

type DetailLog = ResultOf<typeof detailFragment>

// IntegrationHttpLogService appends this when it caps a value, so the start is all that was stored
const TRUNCATED_SUFFIX = '...[truncated]'

const previewClassName =
  'm-0 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded bg-colorFillTertiary p-2 text-xs text-colorText'

// Pins a copy button to the preview's top-end corner, above the scrolled content
const PreviewCopyButton = ({ value }: { value: string }) => (
  <div className='absolute end-2 top-2'>
    <CopyButton value={value} />
  </div>
)

const EmptyPreview = ({ label }: { label: string }) => (
  <div className='flex items-center justify-center rounded bg-colorFillTertiary p-3 text-colorTextSecondary text-xs'>
    {label}
  </div>
)

const JsonPreview = ({ value }: { value: unknown }) => {
  if (value == null) {
    return <EmptyPreview label='No body' />
  }

  return (
    <div className='relative'>
      <pre className={twMerge(previewClassName, 'pe-10')}>{renderJson(value, '')}</pre>
      <PreviewCopyButton value={JSON.stringify(value, null, 2)} />
    </div>
  )
}

// A body is rendered by the content-type of its side of the exchange — a response
// can be HTML, plain text, XML..., not just JSON
const BodyPreview = ({ body, headers }: { body: unknown; headers: unknown }) => {
  if (body == null) {
    return <EmptyPreview label='No body' />
  }

  // Structured value straight from the JSONB column — already parsed, render as JSON
  if (typeof body !== 'string') {
    return <JsonPreview value={body} />
  }

  if (getContentType(headers)?.toLowerCase().includes('json')) {
    try {
      return <JsonPreview value={JSON.parse(body)} />
    } catch {
      // declared as JSON but does not parse — fall through to plain text
    }
  }

  return (
    <div className='flex flex-col gap-1'>
      {body.endsWith(TRUNCATED_SUFFIX) && (
        <Typography.Text type='secondary' className='text-xs'>
          The body was too large to log in full, only its start was stored.
        </Typography.Text>
      )}
      <div className='relative'>
        <pre className={twMerge(previewClassName, 'pe-10')}>{body}</pre>
        <PreviewCopyButton value={body} />
      </div>
    </div>
  )
}

// Must produce the exact text buildCurl returns, just with colored tokens
const CurlPreview = ({ log }: { log: DetailLog }) => (
  <pre className={twMerge(previewClassName, 'max-h-none')}>
    <span className='text-purple7'>curl</span>{' '}
    <span className='text-green7'>'{escapeBody(getAbsoluteUrl(log.url))}'</span>
    {buildCurlChunks(log).map((chunk, index) => {
      const separatorIndex = chunk.indexOf(' ')
      const flag = separatorIndex === -1 ? chunk : chunk.slice(0, separatorIndex)
      const value = separatorIndex === -1 ? null : chunk.slice(separatorIndex + 1)
      return (
        // biome-ignore lint/suspicious/noArrayIndexKey: Static render of a curl snapshot, chunks never reorder
        <Fragment key={index}>
          {' \\\n  '}
          <span className='text-geekblue7'>{flag}</span>
          {value != null && (
            <>
              {' '}
              <span className='text-green7'>{value}</span>
            </>
          )}
        </Fragment>
      )
    })}
  </pre>
)

// Must produce the exact text buildHttp returns, just with colored tokens
const HttpPreview = ({ log }: { log: DetailLog }) => {
  const { method, path, headerEntries, body } = buildHttpParts(log)

  return (
    <pre className={twMerge(previewClassName, 'max-h-none')}>
      <span className='text-purple7'>{method}</span> <span className='text-green7'>{path}</span>{' '}
      <span className='text-orange7'>HTTP/1.1</span>
      {'\n'}
      {headerEntries.map(([key, value], index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: Static render of an HTTP snapshot, headers never reorder
        <Fragment key={index}>
          <span className='text-geekblue7'>{key}</span>
          {': '}
          <span className='text-green7'>{value}</span>
          {'\n'}
        </Fragment>
      ))}
      {body != null && (
        <>
          {'\n'}
          {body}
        </>
      )}
    </pre>
  )
}

const codeSnippetFormats = ['cURL', 'HTTP'] as const
type CodeSnippetFormat = (typeof codeSnippetFormats)[number]

const CodeSnippet = ({ log }: { log: DetailLog }) => {
  const [format, setFormat] = useState<CodeSnippetFormat>('cURL')

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between'>
        <Segmented<CodeSnippetFormat>
          size='small'
          options={[...codeSnippetFormats]}
          value={format}
          onChange={setFormat}
        />
        <CopyButton value={format === 'cURL' ? buildCurl(log) : buildHttp(log)} />
      </div>
      {format === 'cURL' ? <CurlPreview log={log} /> : <HttpPreview log={log} />}
    </div>
  )
}

const sectionAccentClassNames = {
  info: 'border-t-colorInfo',
  success: 'border-t-colorSuccess',
  error: 'border-t-colorError',
} as const

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <span className='pt-1 font-medium text-colorTextSecondary text-xs uppercase tracking-wide'>
    {children}
  </span>
)

// DevTools-style key-value listing — easier to scan than raw JSON
const HeadersPreview = ({ headers }: { headers: unknown }) => {
  const entries = headerEntriesOf(headers)

  if (!entries.length) {
    return <EmptyPreview label='No headers' />
  }

  return (
    <div className='relative'>
      <div className={twMerge(previewClassName, 'flex flex-col gap-0.5 pe-10 font-mono')}>
        {entries.map(([key, value]) => {
          const headerValue = String(value)
          // Only the Authorization header carries a Bearer token — other values with two
          // dots (etags, version strings) would decode as false-positive "JWTs"
          const jwtPayload =
            key.toLowerCase() === 'authorization' && /^bearer\s+/i.test(headerValue)
              ? decodeJwtPayload(headerValue)
              : null
          return (
            <div key={key} className='break-all'>
              <span className='font-medium text-geekblue7'>{key}</span>
              <span className='text-colorTextSecondary'>: </span>
              <span>{String(value)}</span>
              {jwtPayload && (
                <div className='ps-4 text-colorTextSecondary'>
                  ↳ Bearer payload:{' '}
                  <span className='text-colorText'>{renderJson(jwtPayload, INDENT)}</span>
                </div>
              )}
            </div>
          )
        })}
      </div>
      <PreviewCopyButton
        value={entries.map(([key, value]) => `${key}: ${String(value)}`).join('\n')}
      />
    </div>
  )
}

const StatusTag = ({ log }: { log: DetailLog }) =>
  log.statusCode == null ? (
    <Tag className='m-0' color='red'>
      error
    </Tag>
  ) : (
    <Tag className='m-0' color={log.statusCode >= 400 ? 'red' : 'green'}>
      {log.statusCode}
    </Tag>
  )

// The at-a-glance facts of the exchange, so reading the detail doesn't require the table row
const ExpandedSummary = ({ log }: { log: DetailLog }) => {
  const { requestBodySize, responseBodySize } = log

  return (
    <div className='col-span-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-colorBorder bg-colorBgContainer px-3 py-2'>
      <Tag className='m-0' color={methodTagColors[log.method] ?? 'default'}>
        {log.method}
      </Tag>
      <code className='min-w-0 flex-1 break-all text-xs'>{log.url}</code>
      <Tooltip title='Copy request as cURL'>
        <CopyButton value={buildCurl(log)} />
      </Tooltip>
      <Button
        size='small'
        type='text'
        label='Download this log as .jsonl'
        icon={<UntitledIcon icon={ulDownload01} />}
        onClick={() => downloadLogsAsJsonl([log], `integration-http-log-${log.id}.jsonl`)}
      />
      <StatusTag log={log} />
      <span className='whitespace-nowrap text-colorTextSecondary text-xs'>
        {log.durationMs} ms
        {requestBodySize != null && ` · ↑ ${formatBytes(requestBodySize)}`}
        {responseBodySize != null && ` · ↓ ${formatBytes(responseBodySize)}`}
      </span>
    </div>
  )
}

const ExpandedLogSection = ({
  title,
  extra,
  accent,
  children,
}: {
  title: string
  extra?: ReactNode
  accent: keyof typeof sectionAccentClassNames
  children: ReactNode
}) => (
  <section
    className={twMerge(
      // Subgrid: rows are shared with the sibling section, so each Headers/Body pair
      // gets the height of the taller of the two
      'row-span-5 grid min-w-0 grid-rows-subgrid rounded-lg border border-colorBorder border-t-2 bg-colorBgContainer p-3',
      sectionAccentClassNames[accent]
    )}
  >
    <div className='flex items-center justify-between gap-2 border-colorBorder border-b pb-2'>
      <Typography.Text strong>{title}</Typography.Text>
      {extra}
    </div>
    {children}
  </section>
)

type IntegrationHttpLogDetailProps = { id: string }

export const IntegrationHttpLogDetail = ({ id }: IntegrationHttpLogDetailProps) => {
  const { data, loading, error } = useQuery(detailQuery, { variables: { id } })

  if (loading && !data) {
    return <Skeleton className='p-2' />
  }

  // The global Apollo error link already surfaced the failure as a notification
  if (error) {
    return <EmptyPreview label='The log could not be loaded.' />
  }

  if (!data?.integrationHttpLog) {
    return (
      <EmptyPreview label='This log no longer exists, retention may have deleted it already.' />
    )
  }

  return <LoadedLogDetail log={data.integrationHttpLog} />
}

const LoadedLogDetail = ({ log: logRef }: { log: FragmentType<typeof detailFragment> }) => {
  const log = getFragmentData(detailFragment, logRef)
  const requestContentType = getContentType(log.requestHeaders)
  const responseContentType = getContentType(log.responseHeaders)
  const isResponseOk = log.statusCode != null && log.statusCode < 400
  const isNetworkFailure = Boolean(log.errorMessage) && log.statusCode == null

  return (
    <div className='grid grid-cols-2 gap-x-4 gap-y-2 p-2'>
      <ExpandedSummary log={log} />
      <ExpandedLogSection
        title='Request'
        extra={<span className='text-colorTextSecondary text-xs'>{log.provider}</span>}
        accent='info'
      >
        <SectionLabel>Headers ({headerEntriesOf(log.requestHeaders).length})</SectionLabel>
        <HeadersPreview headers={log.requestHeaders} />
        <SectionLabel>{requestContentType ? `Body · ${requestContentType}` : 'Body'}</SectionLabel>
        <BodyPreview body={log.requestBody} headers={log.requestHeaders} />
      </ExpandedLogSection>
      <ExpandedLogSection
        title='Response'
        extra={<StatusTag log={log} />}
        accent={isResponseOk ? 'success' : 'error'}
      >
        <SectionLabel>Headers ({headerEntriesOf(log.responseHeaders).length})</SectionLabel>
        <HeadersPreview headers={log.responseHeaders} />
        {/* A network failure has no response — the error message stands in for the body */}
        {isNetworkFailure ? (
          <>
            <SectionLabel>Error</SectionLabel>
            <pre className='m-0 overflow-auto whitespace-pre-wrap break-all rounded bg-colorErrorBg p-2 text-colorError text-xs'>
              {log.errorMessage}
            </pre>
          </>
        ) : (
          <>
            <SectionLabel>
              {responseContentType ? `Body · ${responseContentType}` : 'Body'}
            </SectionLabel>
            <BodyPreview body={log.responseBody} headers={log.responseHeaders} />
          </>
        )}
      </ExpandedLogSection>
      <section className='col-span-2 flex min-w-0 flex-col gap-2 rounded-lg border border-colorBorder bg-colorBgContainer p-3'>
        <Typography.Text strong>How to reproduce this call</Typography.Text>
        <CodeSnippet log={log} />
      </section>
    </div>
  )
}
